<template>
  <div class="date-range-summary">
            <button class="arrow-btn" @click="(e) => handleArrowClick('previous', e)" @mouseup="(e) => (e.currentTarget as HTMLButtonElement)?.blur()" aria-label="Previous day" title="Previous day" v-html="ICONS.ARROW_LEFT">
    </button>
    <button class="date-label-btn" @click="toggleDropdown" ref="dropdownBtn">
      <span>
        <template v-if="rangeLabel">
          {{ rangeLabel }}
        </template>
        <template v-else>
          {{ formatSummaryDate(startDate) }} – {{ formatSummaryDate(endDate) }}
        </template>
      </span>
      <span class="dropdown-arrow" v-html="ICONS.CHEVRON_DOWN">
      </span>
    </button>
            <button class="arrow-btn" @click="(e) => handleArrowClick('next', e)" @mouseup="(e) => (e.currentTarget as HTMLButtonElement)?.blur()" aria-label="Next day" title="Next day" v-html="ICONS.ARROW_RIGHT">
    </button>
    
    <!-- Calendar Icon Button -->
    <button 
      class="calendar-btn" 
              @click="(e) => handleCalendarClick(e)" 
      @mouseup="(e) => (e.currentTarget as HTMLButtonElement)?.blur()" 
      aria-label="Toggle date selector" 
      title="Toggle date selector"
      v-html="ICONS.GOAL_CALENDAR"
    >
    </button>
    
    <!-- Share Button -->
    <button 
      class="share-btn" 
      @click="(e) => handleShareClick(e)" 
      @mouseup="(e) => (e.currentTarget as HTMLButtonElement)?.blur()" 
      aria-label="Share data" 
      title="Share data"
      v-html="ICONS.SHARE"
    >
    </button>
    
    <!-- Dropdown Menu -->
    <teleport to="body">
      <div
        v-if="isPresetOpen"
        class="preset-menu floating-dropdown"
        :style="dropdownPosition"
        ref="dropdownMenu"
      >
        <div class="preset-group">
          <button @click="() => { setToday(); closeDropdown(); }" class="preset-item" :class="{ 'active': isToday }">Today</button>
          <button @click="() => { setYesterday(); closeDropdown(); }" class="preset-item" :class="{ 'active': isYesterday }">Yesterday</button>
          <button @click="() => { setTomorrow(); closeDropdown(); }" class="preset-item" :class="{ 'active': isTomorrow }">Tomorrow</button>
        </div>
        <div class="preset-group">
          <button @click="() => { setThisWeek(); closeDropdown(); }" class="preset-item" :class="{ 'active': isThisWeek }">This Week</button>
          <button @click="() => { setLastWeek(); closeDropdown(); }" class="preset-item" :class="{ 'active': isLastWeek }">Last Week</button>
          <button @click="() => { setNextWeek(); closeDropdown(); }" class="preset-item" :class="{ 'active': isNextWeek }">Next Week</button>
        </div>
        <div class="preset-group">
          <button @click="() => { setLastNDays(7); closeDropdown(); }" class="preset-item">Last 7 Days</button>
          <button @click="() => { setLastNDays(30); closeDropdown(); }" class="preset-item">Last 30 Days</button>
          <button @click="() => { setLastNDays(90); closeDropdown(); }" class="preset-item">Last 90 Days</button>
        </div>
      </div>
    </teleport>

    <!-- Share Data Modal -->
    <!-- <teleport to="body">
      <ShareDataModal
        :isOpen="isShareModalOpen"
        :startDate="startDate"
        :endDate="endDate"
        @close="closeShareModal"
        @share="handleShareData"
      />
    </teleport> -->
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'
import { 
  getRangeLabel,
  getTodayRange,
  getYesterdayRange,
  getTomorrowRange,
  getThisWeekRange,
  getLastWeekRange,
  getNextWeekRange,
  getLastNDaysRange,
  isTodayRange,
  isYesterdayRange,
  isTomorrowRange,
  isThisWeekRange,
  isLastWeekRange,
  isNextWeekRange
} from '../utils/dateUtils'
import { ICONS } from '../utils/icons'
// import ShareDataModal from './ShareDataModal.vue'

interface Props {
  startDate: string
  endDate: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  previous: []
  next: []
  'update:startDate': [value: string]
  'update:endDate': [value: string]
  update: []
  'toggleDateSelector': []
  'share': [data: { shareMode: string, email?: string, options: { intake: boolean, schedule: boolean, journal: boolean, goals: boolean, aiInsights: boolean }, sendCopyToSelf: boolean }]
  'openShareModal': []
}>()

const rangeLabel = computed(() => getRangeLabel(props.startDate, props.endDate))

// Dropdown state
const isPresetOpen = ref(false)
const dropdownBtn = ref<HTMLButtonElement | null>(null)
const dropdownMenu = ref<HTMLDivElement | null>(null)
const dropdownPosition = ref({})

// Share modal state
// const isShareModalOpen = ref(false)

const toggleDropdown = async () => {
  isPresetOpen.value = !isPresetOpen.value
  await nextTick()
  if (isPresetOpen.value && dropdownBtn.value) {
    const rect = dropdownBtn.value.getBoundingClientRect()
    dropdownPosition.value = {
      position: 'absolute',
      top: `${rect.bottom + window.scrollY}px`,
      left: `${rect.left + window.scrollX}px`,
      zIndex: 1000,
      minWidth: `${rect.width}px`,
    }
  }
  // Force blur to prevent highlighting
  if (dropdownBtn.value) {
    dropdownBtn.value.blur()
  }
}

const closeDropdown = () => {
  isPresetOpen.value = false
}

// Share modal functions
const openShareModal = () => {
  emit('openShareModal')
}

// const closeShareModal = () => {
//   isShareModalOpen.value = false
// }

// Add click handlers that force blur
const handleArrowClick = (direction: 'previous' | 'next', event: MouseEvent) => {
  if (direction === 'previous') {
    emit('previous')
  } else {
    emit('next')
  }
  // Force blur to prevent highlighting
  if (event.currentTarget) {
    (event.currentTarget as HTMLElement).blur()
  }
}

const handleCalendarClick = (event: MouseEvent) => {
  emit('toggleDateSelector')
  // Force blur to prevent highlighting
  if (event.currentTarget) {
    (event.currentTarget as HTMLElement).blur()
  }
}

const handleShareClick = (event: MouseEvent) => {
  openShareModal()
  // Force blur to prevent highlighting
  if (event.currentTarget) {
    (event.currentTarget as HTMLElement).blur()
  }
}

const handleClickOutside = (event: Event) => {
  if (
    isPresetOpen.value &&
    dropdownMenu.value &&
    !dropdownMenu.value.contains(event.target as Node) &&
    dropdownBtn.value &&
    !dropdownBtn.value.contains(event.target as Node)
  ) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

// Preset functions
const setToday = () => {
  const range = getTodayRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setYesterday = () => {
  const range = getYesterdayRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setTomorrow = () => {
  const range = getTomorrowRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setThisWeek = () => {
  const range = getThisWeekRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setLastWeek = () => {
  const range = getLastWeekRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setNextWeek = () => {
  const range = getNextWeekRange()
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

const setLastNDays = (days: number) => {
  const range = getLastNDaysRange(days)
  emit('update:startDate', range.start)
  emit('update:endDate', range.end)
  emit('update')
}

// Computed properties for active states
const isToday = computed(() => isTodayRange(props.startDate, props.endDate))
const isYesterday = computed(() => isYesterdayRange(props.startDate, props.endDate))
const isTomorrow = computed(() => isTomorrowRange(props.startDate, props.endDate))
const isThisWeek = computed(() => isThisWeekRange(props.startDate, props.endDate))
const isLastWeek = computed(() => isLastWeekRange(props.startDate, props.endDate))
const isNextWeek = computed(() => isNextWeekRange(props.startDate, props.endDate))

function formatSummaryDate(dateStr: string): string {
  const date = new Date(dateStr);
  // e.g. "Jun 14, 00:00"
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).replace(/,\s*\d{4}/, ''); // Remove year if present
}
</script>

<style scoped>
.date-range-summary {
  position: relative;
  left: 0;
  width: 100%;
  background: transparent;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  color: white;
  font-weight: 600;
  text-align: center;
  padding: 0;
  box-shadow: none;
  z-index: 10;
  font-size: 1rem;
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0;
  border-bottom: none;
  border-top: none;
  border-radius: 0;
}

.arrow-btn {
  background: rgba(21, 28, 44, 0.85);
  border: 1px solid var(--border-color);
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.45rem 0.7rem;
  border-radius: 0.75rem;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.arrow-btn:hover { border-color: var(--border-hover); color: #e5e7eb; transform: scale(1.06); }

.arrow-btn:active { 
  outline: none; 
  transform: scale(0.95);
}

.arrow-btn:focus { outline: none; color: white; }

.arrow-btn:focus-visible {
  outline: none;
  background: none !important;
}

.arrow-btn:active:focus,
.arrow-btn:active:hover {
  color: white !important;
  background: none !important;
}

.arrow-btn:visited,
.arrow-btn:focus-visible,
.arrow-btn:focus-within {
  background: none !important;
  outline: none !important;
}

/* Keep rounded style consistent when focused */
.arrow-btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }

.date-label-btn {
  background: rgba(21, 28, 44, 0.85);
  border: 1px solid var(--border-color);
  color: white;
  font-weight: 600;
  cursor: pointer;
  padding: 0.45rem 0.85rem;
  border-radius: 0.75rem;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  box-shadow: 0 6px 18px rgba(0,0,0,0.25);
}

.date-label-btn:hover { border-color: var(--border-hover); }

.date-label-btn:focus,
.date-label-btn:active {
  outline: none;
  background-color: transparent;
}

.date-label-btn:focus-visible {
  outline: none;
}

.date-label-btn:active:focus,
.date-label-btn:active:hover {
  background-color: transparent !important;
}

.dropdown-arrow {
  font-size: 0.7rem;
  transition: transform 0.2s ease;
}

.date-label-btn:hover .dropdown-arrow {
  transform: translateY(1px);
}

.calendar-btn {
  background: rgba(21, 28, 44, 0.85);
  border: 1px solid var(--border-color);
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.45rem 0.7rem;
  border-radius: 0.75rem;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.calendar-btn:hover { border-color: var(--border-hover); color: #e5e7eb; transform: scale(1.06); }

.calendar-btn:active { 
  outline: none; 
  transform: scale(0.95);
}

.calendar-btn:focus { outline: none; color: white; }

.calendar-btn:focus-visible {
  outline: none;
  background: none !important;
}

.calendar-btn:active:focus,
.calendar-btn:active:hover {
  color: white !important;
  background: none !important;
}

.calendar-btn:visited,
.calendar-btn:focus-visible,
.calendar-btn:focus-within {
  background: none !important;
  outline: none !important;
}

.calendar-btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }

.share-btn {
  background: rgba(21, 28, 44, 0.85);
  border: 1px solid var(--border-color);
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  padding: 0.45rem 0.7rem;
  border-radius: 0.75rem;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

.share-btn:hover { border-color: var(--border-hover); color: #e5e7eb; transform: scale(1.06); }

.share-btn:active { 
  outline: none; 
  transform: scale(0.95);
}

.share-btn:focus { outline: none; color: white; }

.share-btn:focus-visible {
  outline: none;
  background: none !important;
}

.share-btn:active:focus,
.share-btn:active:hover {
  color: white !important;
  background: none !important;
}

.share-btn:visited,
.share-btn:focus-visible,
.share-btn:focus-within {
  background: none !important;
  outline: none !important;
}

.share-btn:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 2px; }

/* Dropdown Menu Styles */
.floating-dropdown {
  position: absolute !important;
}

.preset-menu {
  background: rgb(21, 28, 44);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.5),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  width: max-content;
  min-width: 220px;
  max-width: 98vw;
  z-index: 20001;
  box-sizing: border-box;
  padding: 0;
}

.preset-group {
  padding: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.preset-group:last-child {
  border-bottom: none;
}

.preset-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.75rem 1rem;
  text-align: left;
  border: none;
  background: none;
  color: var(--text-color);
  font-size: 0.95rem;
  cursor: pointer;
  border-radius: 0;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.preset-item:hover { background-color: rgba(255, 255, 255, 0.1); }

.preset-item.active {
  background-color: rgba(255, 255, 255, 0.08);
  color: white;
}

@media (max-width: 768px) {
  .date-range-summary {
    gap: 0.25rem;
  }
  
  .arrow-btn, 
  .calendar-btn, 
  .share-btn {
    padding: 0.35rem 0.5rem;
    font-size: 0.9rem;
    border-radius: 0.5rem;
  }
  
  .date-label-btn {
    padding: 0.35rem 0.6rem;
    font-size: 0.9rem;
    border-radius: 0.5rem;
  }
}
</style> 